import React from "react";
import { EmptyState, EmptyStateProps } from "../../../components/ui";
import { AccountName, formatRupees } from "../../users";
import { PAYMENT_STATUS_STYLES } from "../../revenue/paymentStyles";
import type { SubscriptionActivity } from "../types/dashboard.types";

const COLUMNS = [
  { heading: "Account", width: "w-[30%]" },
  { heading: "Plan", width: "w-[18%]" },
  { heading: "Amount", width: "w-[16%]" },
  { heading: "Date", width: "w-[18%]" },
  { heading: "Status", width: "w-[18%]" },
];

interface SubscriptionActivityTableProps {
  subscriptions: SubscriptionActivity[];
  /** Stands in for the body when there is nothing in the feed. */
  empty: EmptyStateProps;
  /** Opens the account behind the row. Every row leads somewhere, so the whole
   *  row is the target rather than the name alone — a four-pixel-tall link in a
   *  fifty-pixel row is a worse thing to aim at than the row. */
  onSelect?: (subscription: SubscriptionActivity) => void;
}

/** The latest subscription payments, newest first — five of them at most; the
 *  cap is the service's, see `RECENT_ACTIVITY_COUNT`. */
const SubscriptionActivityTable: React.FC<SubscriptionActivityTableProps> = ({
  subscriptions,
  empty,
  onSelect,
}) => {
  const cellClasses = "px-4 sm:px-6 py-4 text-[14px] text-ink";

  return (
    <div className="overflow-x-auto custom-scrollbar-overlay">
      <table className="w-full table-fixed border-collapse text-left min-w-[760px]">
        <colgroup>
          {COLUMNS.map((column) => (
            <col key={column.heading} className={column.width} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.heading}
                scope="col"
                className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
              >
                {column.heading}
              </th>
            ))}
          </tr>
        </thead>

        {subscriptions.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={COLUMNS.length}>
                <EmptyState {...empty} />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {subscriptions.map((subscription) => (
              <tr
                key={subscription.id}
                onClick={() => onSelect?.(subscription)}
                className={`border-b border-line last:border-0 transition-colors ${
                  onSelect ? "hover:bg-[#F8FEFF] cursor-pointer" : ""
                }`}
              >
                <td className="px-4 sm:px-6 py-4">
                  <AccountName name={subscription.accountName} />
                </td>
                <td className={`${cellClasses} whitespace-nowrap`}>
                  {subscription.plan}
                </td>
                <td className={`${cellClasses} whitespace-nowrap`}>
                  {formatRupees(subscription.amount)}
                </td>
                <td className={`${cellClasses} whitespace-nowrap`}>
                  {subscription.date}
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <span
                    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                      PAYMENT_STATUS_STYLES[subscription.status]
                    }`}
                  >
                    {subscription.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default SubscriptionActivityTable;
